import React, { useCallback, useState } from "react";
import {
  ReCaptcha,
  RecaptchaDisclaimer,
  ReCaptchaProvider,
} from "@wss/re-captcha";
import Modal from "../Modal";
import PriceGuaranteeModal from "./PriceGuaranteeModal";
import {
  FormSubmitErrors,
  FormSubmitStatuses,
  PriceMatchResponse,
} from "./PriceGuaranteeModalDefinitions";

function getEmptyErrors() {
  return {
    email: "",
    phoneNumber: "",
    priceToMatch: "",
    screenshot: "",
    externalLink: "",
  };
}

interface PriceGuaranteeModalContainerProps {
  modalOpen: boolean;
  onClose: () => void;
  itemNumber: string;
}

interface ItemNumberCacheProperties {
  email: string;
  phoneNumber: string;
  priceToMatch: string;
  externalLink: string;
  screenshot?: File | null;
  submitStatus: string;
  itemNumber: string;
  errors: {
    email: string;
    phoneNumber: string;
    priceToMatch: string;
    screenshot: string;
    externalLink: string;
    global?: string;
  };
}

interface PriceGuaranteModalContainerState extends ItemNumberCacheProperties {
  recaptchaValue: string | null;
  itemNumberCache: {
    [key: string]: Record<string, never> | ItemNumberCacheProperties;
  };
}

export default function PriceGuaranteeModalContainer({
  modalOpen,
  onClose,
  itemNumber,
}: PriceGuaranteeModalContainerProps) {
  const screenshotAllowedExtensions = [
    ".jpeg",
    ".jpg",
    ".png",
    ".doc",
    ".docx",
    ".xml",
    ".odt",
    ".pdf",
  ];

  const emptyCacheKey = "__empty__";

  const [state, setState] = useState<PriceGuaranteModalContainerState>({
    email: "",
    phoneNumber: "",
    priceToMatch: "",
    externalLink: "",
    screenshot: null,
    recaptchaValue: null,
    submitStatus: FormSubmitStatuses.EDITING,
    itemNumber: itemNumber || emptyCacheKey,
    errors: getEmptyErrors(),
    itemNumberCache: {
      [emptyCacheKey]: {},
    },
  });

  const regex = {
    email: /^.+@[^.@]+\..+$/,
    externalLink: /[A-z0-9_-]+\.[A-z0-9_-]+/,
    screenshotName: new RegExp(
      `${screenshotAllowedExtensions.map((x) => `\\${x}`).join("|")}$`,
      "i",
    ),
  };

  const validateFormData = () => {
    const { email, priceToMatch, externalLink, screenshot } = state;

    let isValid = true;
    const newErrors = getEmptyErrors();

    if (!regex.email.test(email)) {
      isValid = false;
      newErrors.email = "Please enter a valid email address";
    }

    if (Number.isNaN(priceToMatch) || Number(priceToMatch) <= 0) {
      isValid = false;
      newErrors.priceToMatch = "Please enter a valid price";
    }

    if (!externalLink && !screenshot) {
      isValid = false;
      const requiredVerificationMessage =
        "Please include a link to the website with the lower price or a screenshot of the lower price";
      newErrors.externalLink = requiredVerificationMessage;
      newErrors.screenshot = requiredVerificationMessage;
    } else {
      if (externalLink && !regex.externalLink.test(externalLink)) {
        isValid = false;
        newErrors.externalLink =
          "Please enter a valid link to the website with the lower price";
      }

      if (screenshot && !regex.screenshotName.test(screenshot.name)) {
        isValid = false;
        newErrors.screenshot =
          "Please include an image, document, or PDF displaying the lower price";
      }
    }

    setState({ ...state, errors: { ...state.errors, ...newErrors } });

    return isValid;
  };

  const submitFormData = async () => {
    const {
      itemNumber,
      email,
      phoneNumber,
      priceToMatch,
      externalLink,
      screenshot,
      recaptchaValue,
    } = state;

    const errors = getEmptyErrors();
    let submitStatus = FormSubmitStatuses.INPROGRESS;
    let errorMessage = "";

    const formData = new FormData();
    formData.append("emailAddress", email);
    formData.append("phoneNumber", phoneNumber);
    formData.append("citedLowerPrice", priceToMatch);
    formData.append("linkToCitedLowerPrice", externalLink);
    if (typeof recaptchaValue === "string") {
      formData.append("recaptchaResponse", recaptchaValue);
    }
    formData.append("itemNumber", itemNumber);

    if (screenshot) formData.append("imageOfLowerPrice", screenshot);

    setState({ ...state, submitStatus, errors });

    try {
      const response = await fetch("/quotes:pricematch/", {
        method: "post",
        body: formData,
      });

      if (!response.ok) {
        errorMessage = FormSubmitErrors.DEFAULT;
      } else {
        const responseData = await response.json<PriceMatchResponse>();

        if (!responseData.success) {
          if (responseData.validity && !responseData.validity.recaptcha) {
            errorMessage = FormSubmitErrors.TRY_AGAIN;
          } else {
            errorMessage = FormSubmitErrors.VALIDATE;
          }
        }
      }
    } catch (e) {
      errorMessage = FormSubmitErrors.DEFAULT;
    }

    if (errorMessage) {
      submitStatus = FormSubmitStatuses.EDITING;
    } else {
      submitStatus = FormSubmitStatuses.COMPLETE;
    }

    setState({
      ...state,
      submitStatus,
      errors: { ...errors, global: errorMessage },
    });
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState({ ...state, email: e.target.value });
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState({ ...state, phoneNumber: e.target.value });
  };

  const handlePriceToMatchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState({ ...state, priceToMatch: e.target.value });
  };

  const handleExternalLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState({ ...state, externalLink: e.target.value });
  };

  const handleScreenshotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const screenshot = e.target.files ? e.target.files[0] : null;
    setState({ ...state, screenshot });
  };

  const handleRecaptchaChange = useCallback(function onReCaptchaChange(
    recaptchaValue: string,
  ) {
    setState((state) => ({ ...state, recaptchaValue }));
  }, []);

  const handleSubmitButtonClick = () => {
    if (!validateFormData()) return;

    submitFormData();
  };

  const {
    email,
    phoneNumber,
    priceToMatch,
    externalLink,
    screenshot,
    submitStatus,
    errors,
  } = state;

  let readOnly;
  let submitSuccessMessage = "";

  switch (submitStatus) {
    case FormSubmitStatuses.EDITING:
      readOnly = false;
      break;
    case FormSubmitStatuses.COMPLETE:
      readOnly = true;
      submitSuccessMessage = "Lowest Price Guarantee submitted successfully";
      break;
    case FormSubmitStatuses.INPROGRESS:
    default:
      readOnly = true;
      break;
  }

  return (
    <Modal
      title="Lowest Guaranteed Price"
      className="modal--md"
      isOpen={modalOpen}
      onClose={onClose}
      customFooter={
        <>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmitButtonClick}
            disabled={readOnly}
          >
            Submit Lowest Price Guarantee
          </button>
          <RecaptchaDisclaimer />
        </>
      }
    >
      <PriceGuaranteeModal
        readOnly={readOnly}
        email={email}
        phoneNumber={phoneNumber}
        priceToMatch={priceToMatch}
        externalLink={externalLink}
        screenshotName={screenshot ? screenshot.name : "No files chosen"}
        screenshotAllowedMimeTypes={screenshotAllowedExtensions}
        errors={errors}
        submitSuccessMessage={submitSuccessMessage}
        onEmailChange={handleEmailChange}
        onPhoneNumberChange={handlePhoneNumberChange}
        onPriceToMatchChange={handlePriceToMatchChange}
        onExternalLinkChange={handleExternalLinkChange}
        onScreenshotChange={handleScreenshotChange}
      />

      <ReCaptchaProvider>
        <ReCaptcha onVerify={handleRecaptchaChange} />
      </ReCaptchaProvider>
    </Modal>
  );
}
